import { DotsHorizontalIcon } from '@radix-ui/react-icons'
import type { BackofficeUser } from 'api/generated'
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Checkbox } from 'components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/ui/tooltip'
import {
  clientSummary,
  formatDay,
  LOGIN_METHOD_NAMES,
  timeAgo,
} from 'helpers/user-details'
import { SEGMENT_LABELS, SOURCE_LABELS } from 'helpers/user-filters'
import { cn } from 'lib/utils'
import {
  Ban,
  ExternalLink,
  Globe,
  HelpCircle,
  Mail,
  PanelRightOpen,
  ShieldCheck,
  Smartphone,
  Trash2,
} from 'lucide-react'
import type { UserRow } from 'store/users/usersSlice'
import type { UserSearchInfo } from 'types/users'
import { PremiumExpirationPicker } from 'ui/components/PremiumExpirationPicker'

export const SITE_URL = 'https://www.tennistalker.it'

/** What a user row can do, wired by the users page. */
export interface UserRowHandlers {
  onOpen: (user: UserRow) => void
  onDelete: (user: UserRow) => void
  onBan: (userId: number, notes: string) => void
  onUnban: (userId: number) => void
  onSetPremiumUser: (userId: number) => void
  onSetPremiumExpirationDate: (userId: number, date: string) => void
  onSendConfirmationEmail: (email: string) => void
  onPasswordReset: (email: string) => void
}

const initials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')

/** A backoffice user, with the search data when it is known. */
type UserWithSearch = BackofficeUser & { search?: UserSearchInfo }

export const UserAvatar = ({
  user,
  className,
}: {
  user: UserWithSearch
  className?: string
}) => {
  const photo = user.search?.photo ?? user.photo?.user?.url
  return (
    <Avatar className={cn('size-10', className)}>
      {photo && <AvatarImage src={photo} alt='' />}
      <AvatarFallback>{initials(user.full_name)}</AvatarFallback>
    </Avatar>
  )
}

/** Account state badges: segment, premium, ban, confirmation. */
export const UserBadges = ({ user }: { user: UserWithSearch }) => {
  const segment = user.search?.primary_segment ?? user.primary_segment
  return (
    <div className='flex flex-wrap gap-1'>
      {segment && <Badge variant='outline'>{SEGMENT_LABELS[segment]}</Badge>}
      {user.search?.admin?.premium && (
        <Badge className='bg-lime-600 text-white'>Premium</Badge>
      )}
      {user.role !== 'simple_user' && user.role !== 'premium_user' && (
        <Badge variant='secondary'>{user.role}</Badge>
      )}
      {user.is_banned && <Badge variant='destructive'>Bannato</Badge>}
      {!user.activated && <Badge variant='secondary'>Non confermato</Badge>}
    </div>
  )
}

/** Avatar, name (opens the dialog), ID, email and badges. */
export const UserIdentity = ({
  user,
  onOpen,
}: {
  user: UserRow
  onOpen: (user: UserRow) => void
}) => (
  <div className='flex min-w-0 items-start gap-3'>
    <UserAvatar user={user} />
    <div className='min-w-0 space-y-1'>
      <button
        type='button'
        onClick={() => onOpen(user)}
        className='block max-w-full truncate text-left font-medium hover:underline'
      >
        {user.full_name || '(senza nome)'}
      </button>
      <div className='truncate text-xs text-muted-foreground'>
        #{user.id} · {user.email}
      </div>
      <UserBadges user={user} />
    </div>
  </div>
)

/** The linked FIT player: name, rank, club and category. */
export const FitPlayerSummary = ({ user }: { user: UserRow }) => {
  const player = user.search.fit_player_profile
  if (!player)
    return <span className='text-sm text-muted-foreground'>Nessun FIT</span>
  return (
    <div className='min-w-0 space-y-0.5 text-sm'>
      <div className='flex items-center gap-1.5'>
        {player.rank && (
          <Badge variant='outline' className='font-mono'>
            {player.rank}
          </Badge>
        )}
        <a
          href={`${SITE_URL}/giocatore/${player.id}`}
          target='_blank'
          rel='noopener noreferrer'
          className='truncate hover:underline'
        >
          {player.full_name}
        </a>
      </div>
      <div className='truncate text-xs text-muted-foreground'>
        {[
          player.club &&
            `${player.club.name}${player.club.province ? ` (${player.club.province})` : ''}`,
          player.age_category,
          user.search.admin?.fit_player_id &&
            `tessera ${user.search.admin.fit_player_id}`,
          !player.active && 'non tesserato quest’anno',
        ]
          .filter(Boolean)
          .join(' · ')}
      </div>
    </div>
  )
}

const CLIENT_ICONS = { app: Smartphone, web: Globe }

/** App or web with version, last activity and login method. */
export const ClientSummary = ({ user }: { user: UserRow }) => {
  const admin = user.search.admin
  const client = clientSummary(admin)
  const Icon = client.kind ? CLIENT_ICONS[client.kind] : HelpCircle
  return (
    <div className='space-y-0.5 text-sm'>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 font-medium',
              client.kind === 'app' &&
                'bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-200',
              client.kind === 'web' &&
                'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200',
              !client.kind && 'text-muted-foreground'
            )}
          >
            <Icon className='h-3.5 w-3.5' />
            {client.label}
          </div>
        </TooltipTrigger>
        <TooltipContent>{client.source}</TooltipContent>
      </Tooltip>
      {admin && (
        <div className='text-xs text-muted-foreground'>
          {admin.last_active_at
            ? `Attivo ${timeAgo(admin.last_active_at)}`
            : 'Nessuna sessione attiva'}
          {admin.last_sign_in_method &&
            ` · login ${LOGIN_METHOD_NAMES[admin.last_sign_in_method]}`}
        </div>
      )}
    </div>
  )
}

/** Sign-up date and where it happened. */
export const SignupSummary = ({ user }: { user: UserRow }) => {
  const admin = user.search.admin
  return (
    <div className='text-sm'>
      {formatDay(user.created_at)}
      <div className='text-xs text-muted-foreground'>
        {[
          admin?.signup_source && SOURCE_LABELS[admin.signup_source],
          admin?.signup_method && LOGIN_METHOD_NAMES[admin.signup_method],
          admin?.login_method === 'social' && !admin.signup_method && 'social',
        ]
          .filter(Boolean)
          .join(' · ') || timeAgo(user.created_at)}
      </div>
    </div>
  )
}

/** Premium flag and expiration, editable in place. */
export const PremiumControls = ({
  user,
  handlers,
}: {
  user: UserRow
  handlers: UserRowHandlers
}) => (
  <div className='flex items-center gap-2'>
    {/* The label widens the touch target of the 16px checkbox on mobile */}
    <label
      className='-m-2 flex cursor-pointer items-center gap-1.5 p-2 text-sm'
      title='Flag premium'
    >
      <Checkbox
        checked={user.premium}
        disabled={user.loaders.premiumUser}
        onCheckedChange={() => handlers.onSetPremiumUser(user.id)}
      />
      <span className='md:sr-only'>Premium</span>
    </label>
    <PremiumExpirationPicker
      value={user.premium_expiration}
      loading={user.loaders.premiumExpirationDate}
      onChange={(date) => handlers.onSetPremiumExpirationDate(user.id, date)}
    />
  </div>
)

export const UserRowActions = ({
  user,
  handlers,
}: {
  user: UserRow
  handlers: UserRowHandlers
}) => (
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger asChild>
      <Button
        variant='ghost'
        className='h-8 w-8 p-0 data-[state=open]:bg-muted'
        disabled={user.loaders.isBanned}
      >
        <DotsHorizontalIcon className='h-4 w-4' />
        <span className='sr-only'>Azioni</span>
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align='end' className='w-56'>
      <DropdownMenuItem onClick={() => handlers.onOpen(user)}>
        <PanelRightOpen /> Apri scheda
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <a
          href={`${SITE_URL}/utente/${user.id}`}
          target='_blank'
          rel='noopener noreferrer'
        >
          <ExternalLink /> Profilo sul sito
        </a>
      </DropdownMenuItem>
      {!user.activated && (
        <DropdownMenuItem
          onClick={() => handlers.onSendConfirmationEmail(user.email)}
        >
          <Mail /> Rimanda mail di conferma
        </DropdownMenuItem>
      )}
      <DropdownMenuItem onClick={() => handlers.onPasswordReset(user.email)}>
        <Mail /> Reset password
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      {user.is_banned ? (
        <DropdownMenuItem onClick={() => handlers.onUnban(user.id)}>
          <ShieldCheck /> Rimuovi ban
        </DropdownMenuItem>
      ) : (
        <DropdownMenuItem
          onClick={() => {
            const notes = prompt('Motivazione del ban')
            if (notes) handlers.onBan(user.id, notes)
          }}
        >
          <Ban /> Banna
        </DropdownMenuItem>
      )}
      <DropdownMenuItem
        onClick={() => handlers.onDelete(user)}
        className='text-red-500!'
      >
        <Trash2 /> Elimina
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
)

/** The whole row as a card, for phones where the table doesn't fit. */
export const UserMobileCard = ({
  user,
  handlers,
}: {
  user: UserRow
  handlers: UserRowHandlers
}) => (
  <div
    className={cn(
      'space-y-3 rounded-lg border p-3',
      user.is_banned && 'border-red-300 bg-red-50 dark:bg-red-500/10',
      !user.activated && 'bg-violet-50 dark:bg-muted'
    )}
  >
    <div className='flex items-start justify-between gap-2'>
      <UserIdentity user={user} onOpen={handlers.onOpen} />
      <UserRowActions user={user} handlers={handlers} />
    </div>
    <div className='grid grid-cols-2 gap-3'>
      <ClientSummary user={user} />
      <SignupSummary user={user} />
    </div>
    <FitPlayerSummary user={user} />
    {user.is_banned && user.ban_notes && (
      <p className='text-xs text-red-700 dark:text-red-300'>
        Ban: {user.ban_notes}
      </p>
    )}
    <PremiumControls user={user} handlers={handlers} />
  </div>
)
